import React, { useState } from "react"
import {
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  styled
} from "@mui/material"
import { ThickDivider } from "./ThickDivider"
import { InstructionsDialog } from "src/components/InstructionsDialog"
import {
  Comment,
  downloadJobFiles,
  downloadSingleJobFiles,
  getComments,
  getReferenceFiles,
  MultilangJob,
  putComment,
  putReferenceFiles,
  ReferenceFile,
  reopenSingleLangJob,
  SingleLangJob
} from "src/services/api/document"
import { useTranslation } from "react-i18next"
import { scrollbarMixin } from "src/providers/AppProviders"
import { useResponsive } from "src/utils/useResponsive"
import { StatusChip } from "src/components/table/StatusChip"

const StageChip = styled(Chip)(({ theme }) => ({
  border: "1px solid #E7E7E7",
  borderRadius: "5px",
  fontWeight: 500,
  fontSize: "0.8rem",
  "& .MuiChip-label": { p: theme.spacing(1, 2) }
}))

StageChip.defaultProps = { size: "small", variant: "outlined" }

export { StageChip }

export const SingleLangJobs = ({
  multilangJob,
  onReopenClick,
  overflowAuto
}: {
  multilangJob: MultilangJob
  onReopenClick: () => void
  overflowAuto: boolean
}) => {
  const { t } = useTranslation("multilang")

  const downloadFiles = () => {
    downloadJobFiles(multilangJob)
  }

  const overflow = overflowAuto ? { overflowY: "auto" } : {}
  const isFinished = multilangJob.status === "FINISHED"

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 8 }, pt: { xs: 2, md: 4 }, ...overflow }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">{t("delivery")}</Typography>
        <Button color="secondary" variant="outlined" onClick={downloadFiles} disabled={!multilangJob.resultFilesReady} >
          {t("downloadAll")}
        </Button>
      </Stack>
      <ThickDivider />
      <TableContainer sx={{ ...scrollbarMixin("medium") }}>
        <Table size="small">
          <TableHead sx={{
            "& .MuiTableCell-head": {
              fontWeight: 600
            }
          }}>
            <TableRow>
              <TableCell>{t("language")}</TableCell>
              {!isFinished && <TableCell>{t("stage")}</TableCell>}
              {!isFinished && <TableCell>{t("status")}</TableCell>}
              <TableCell>{t("action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {multilangJob.singleLangJobs.map((j) => (
              <SingleLanguageRow
                key={j.jobId}
                multilangJob={multilangJob}
                singleLangJob={j}
                onReopenClick={onReopenClick}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

interface SingleLanguageRowProps {
  multilangJob: MultilangJob
  singleLangJob: SingleLangJob
  onReopenClick: () => void
}

const SingleLanguageRow = ({ multilangJob, singleLangJob, onReopenClick }: SingleLanguageRowProps) => {
  const { t: tLang } = useTranslation("lang")
  const { t } = useTranslation("multilang")
  const { t: tCommon } = useTranslation("common")
  const [instructionsOpened, setInstructionsOpened] = useState(false)
  const [initialComments, setInitialComments] = useState<Comment[]>()
  const [initialReferenceFiles, setInitialReferenceFiles] = useState<ReferenceFile[]>()
  const { isMobile } = useResponsive()
  const { palette } = useTheme()

  const isMultilangFinished = multilangJob.status === "FINISHED"

  const isCompleted = singleLangJob.status === "FINISHED"
  const isCancelled = singleLangJob.status === "CANCELLED"
  const fontColor = isCancelled ? palette.text.secondary : palette.text.primary
  const backgroundColor = isCompleted ? "#BEFFFB" : isCancelled ? "#E0E0E0" : "#A4E9FF"

  const reopen = () => {
    reopenSingleLangJob(multilangJob.id, singleLangJob.jobId).then(() => onReopenClick?.())
  }

  const onInstructionsClick = () => {
    getComments(multilangJob.id, singleLangJob.jobId).then((comments) => setInitialComments(comments))
    getReferenceFiles(multilangJob.id, singleLangJob.jobId).then((referenceFiles) =>
      setInitialReferenceFiles(referenceFiles)
    )
    setInstructionsOpened(true)
  }

  const addReferenceFiles = (files: string[]) => {
    putReferenceFiles(multilangJob.id, singleLangJob.jobId, files)
  }

  const addComment = (comment: string) => {
    putComment(multilangJob.id, singleLangJob.jobId, comment)
  }

  const download = () => {
    downloadSingleJobFiles(multilangJob, singleLangJob)
  }

  return (
    <>
      {initialComments && initialReferenceFiles && (
        <InstructionsDialog
          open={instructionsOpened}
          initialComments={initialComments}
          initialReferenceFiles={initialReferenceFiles}
          onSubmit={(referenceFiles, comment) => {
            setInstructionsOpened(false)
            referenceFiles.length > 0 && addReferenceFiles(referenceFiles.map((f) => f.id))
            comment && addComment(comment)
          }}
          onDismiss={() => setInstructionsOpened(false)}
        />
      )}

      <TableRow sx={{ "&:last-child td": { borderBottom: 0 } }}>
        <TableCell>
          <Typography noWrap>{tLang(singleLangJob.targetLanguage.toLowerCase())}</Typography>
        </TableCell>

        {!isMultilangFinished && (
          <TableCell>
            {singleLangJob.currentStage && !["FINISHED", "CANCELLED"].includes(singleLangJob.status) ? (
              <StageChip label={t(`stage_${singleLangJob.currentStage}`)} />
            ) : (
              <Typography color="text.secondary">-</Typography>
            )}
          </TableCell>
        )}

        {!isMultilangFinished && (
          <TableCell>
            <StatusChip label={tCommon(`files.${singleLangJob.status}`).toUpperCase()}
              backgroundColor={backgroundColor}
              fontColor={fontColor} />
          </TableCell>
        )}

        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              disabled={!singleLangJob.resultFilesReady}
              onClick={download}
            >
              {t("download")}
            </Button>
            {singleLangJob.canReopen && (
              <Button variant="underlined" size="small" onClick={reopen}>
                {t("reopen")}
              </Button>
            )}
            {singleLangJob.status === "IN_PROGRESS" && !isMobile && (
              <Button variant="underlined" size="small" onClick={onInstructionsClick} disableRipple={true}>
                {t("instructions")}
              </Button>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    </>
  )
}
